import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import logo from '../traderhub24-logo.svg'
import { useNotification } from './contexts/NotificationContext'
import { supabase } from './lib/supabase'

type PublicTrade = {
  symbol: string
  market: 'Commodity' | 'Crypto' | 'Forex' | 'Stock'
  trade_type: 'spot' | 'future'
  long_short: 'long' | 'short'
  quantity: number | null
  contract_size: number | null
  entry_date: string | null
  entry_price: number
  stop_loss: number | null
  take_profit: number | null
  rr: number | null
  exit_date: string | null
  exit_price: number | null
  trade_setup: string | null
  notes: string | null
  strategy: string | null
  currency: string | null
  position_size: number | null
  pnl: number | null
  pnl_percentage: number | null
  total_entry_amount?: number | null
  total_exit_amount?: number | null
  entry_setup_image: string | null
  exit_setup_image: string | null
  time_interval: string | null
  public_show_quantity: boolean
  current_price?: number | null
  current_price_updated_at?: string | null
  previous_close_price?: number | null
  unrealized_pnl?: number | null
  unrealized_pnl_percentage?: number | null
  daily_change_percentage?: number | null
}

type PublicHistoryEvent = {
  event_type: string
  description: string
  price: number | null
  mood: string | null
  metadata: any
  created_at: string
}

type PublicComment = {
  text: string
  mood: string | null
  created_at: string
}

type PublisherProfile = {
  display_name?: string | null
  biography?: string | null
  x_profile?: string | null
  instagram_profile?: string | null
  telegram_profile?: string | null
  youtube_profile?: string | null
}

type PublicTradeBundle = {
  trade: PublicTrade
  history: PublicHistoryEvent[]
  comments: PublicComment[]
  public_slug: string | null
  publisher?: PublisherProfile | null
}

const formatDateTime = (dateString: string | null) => {
  if (!dateString) return '-'
  const d = new Date(dateString)
  if (Number.isNaN(d.getTime())) return '-'
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatNumber = (value: number | null, currency?: string | null, digits = 2) => {
  if (value === null || Number.isNaN(value)) return '-'
  const formatted = value.toFixed(digits).replace('.', ',')
  return currency ? `${formatted} ${currency}` : formatted
}

function titleizeEventType(eventType: string) {
  return eventType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

export default function PublicTrade() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const { showSuccess } = useNotification()
  const { t } = useTranslation('pages')

  const [bundle, setBundle] = useState<PublicTradeBundle | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const showQuantity = Boolean(bundle?.trade?.public_show_quantity)

  const averageCost = useMemo(() => {
    if (!bundle?.trade) return null
    const trade = bundle.trade
    const history = [...bundle.history].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    )
    const qtyEvents = history.filter((e) =>
      ['entry', 'add', 'scale_in', 'reduce_size', 'scale_out'].includes(e.event_type)
    )
    let cost = 0
    let qty = 0
    const currentQty = trade.quantity ?? 0
    const sumAdd = qtyEvents
      .filter((e) => e.event_type === 'add' || e.event_type === 'scale_in')
      .reduce((s, e) => s + (e.metadata?.quantity ?? 0), 0)
    const sumReduce = qtyEvents
      .filter((e) => e.event_type === 'reduce_size' || e.event_type === 'scale_out')
      .reduce((s, e) => s + (e.metadata?.quantity ?? 0), 0)
    const hasEntryEvent = qtyEvents.some((e) => e.event_type === 'entry')
    if (!hasEntryEvent && (sumAdd > 0 || sumReduce > 0)) {
      const initialQty = currentQty + sumReduce - sumAdd
      if (initialQty > 0) {
        cost = (trade.entry_price ?? 0) * initialQty
        qty = initialQty
      }
    }
    for (const e of qtyEvents) {
      const eventQty = e.metadata?.quantity ?? 0
      const price = e.price ?? trade.entry_price ?? 0
      if (e.event_type === 'entry') {
        if (eventQty > 0) {
          cost = price * eventQty
          qty = eventQty
        }
      } else if (e.event_type === 'add' || e.event_type === 'scale_in') {
        if (eventQty > 0) {
          cost += price * eventQty
          qty += eventQty
        }
      } else if (
        (e.event_type === 'reduce_size' || e.event_type === 'scale_out') &&
        eventQty > 0 &&
        qty > 0
      ) {
        const proportionalCost = (cost / qty) * Math.min(eventQty, qty)
        cost -= proportionalCost
        qty -= Math.min(eventQty, qty)
      }
    }
    return qty > 0 ? cost / qty : trade.entry_price
  }, [bundle])

  const realizedRR = useMemo(() => {
    if (!bundle?.trade) return null
    const { entry_price: entryPrice, stop_loss: stopLoss, exit_price: exitPrice, long_short: direction } = bundle.trade
    if (exitPrice === null || stopLoss === null) return null
    const risk = Math.abs(entryPrice - stopLoss)
    if (risk === 0) return null
    const reward =
      direction === 'long'
        ? exitPrice - entryPrice
        : entryPrice - exitPrice
    return reward / risk
  }, [bundle])

  const isRealizedProfit = realizedRR !== null && realizedRR >= 0

  const isUuid = useMemo(() => {
    if (!slug) return false
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(slug)
  }, [slug])

  const shareUrl = useMemo(() => {
    if (!slug) return null
    if (typeof window === 'undefined') return null
    // Avoid encouraging sharing of UUID-based community links
    if (isUuid) return null
    return `${window.location.origin}/p/${slug}`
  }, [slug, isUuid])

  useEffect(() => {
    const run = async () => {
      if (!slug) {
        setError('Invalid link')
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        if (isUuid) {
          // 1) Community visibility bundle (works for anon + authed)
          const { data: communityData, error: communityErr } = await supabase.rpc(
            'get_public_trade_bundle_by_id',
            { p_trade_id: slug },
          )
          if (communityErr) throw communityErr
          if (communityData) {
            setBundle(communityData as PublicTradeBundle)
            setError(null)
            return
          }

          // 2) Restricted visibility bundle (followers-only; requires auth)
          const { data: restrictedData, error: restrictedErr } = await supabase.rpc(
            'get_restricted_trade_bundle_by_id',
            { p_trade_id: slug },
          )
          if (restrictedErr) throw restrictedErr
          if (restrictedData) {
            setBundle(restrictedData as PublicTradeBundle)
            setError(null)
            return
          }

          setBundle(null)
          setError('This trade is not available (it may have been made private).')
          return
        }

        const { data, error: rpcError } = await supabase.rpc('get_public_trade_bundle', { p_slug: slug })

        if (rpcError) throw rpcError
        if (!data) {
          setBundle(null)
          setError('This trade is not available (it may have been made private).')
          return
        }

        setBundle(data as PublicTradeBundle)
        setError(null)
      } catch (err: any) {
        setBundle(null)
        setError(err.message || 'Failed to load public trade')
      } finally {
        setLoading(false)
      }
    }

    run()
  }, [slug, isUuid])

  // SEO: Update document title and meta tags dynamically
  useEffect(() => {
    if (!bundle?.trade) {
      document.title = 'TraderHub24 - Public Trade'
      return
    }

    const symbol = bundle.trade.symbol
    const status = bundle.trade.exit_date ? 'closed' : 'open'
    const title = `TraderHub24 - ${symbol} ${status} trade.`
    
    document.title = title

    // Update or create meta description
    let metaDescription = document.querySelector('meta[name="description"]')
    if (!metaDescription) {
      metaDescription = document.createElement('meta')
      metaDescription.setAttribute('name', 'description')
      document.head.appendChild(metaDescription)
    }
    const description = showQuantity
      ? `View ${symbol} ${status} trade details on TraderHub24. Entry: ${formatNumber(bundle.trade.entry_price, bundle.trade.currency)}, P&L: ${formatNumber(bundle.trade.pnl, bundle.trade.currency)}.`
      : `View ${symbol} ${status} trade details on TraderHub24. Entry: ${formatNumber(bundle.trade.entry_price, bundle.trade.currency)}, P&L %: ${
          bundle.trade.pnl_percentage !== null ? `${bundle.trade.pnl_percentage.toFixed(2)}%` : '-'
        }.`
    metaDescription.setAttribute('content', description)

    // Update or create Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]') || document.createElement('meta')
    if (!document.querySelector('meta[property="og:title"]')) {
      ogTitle.setAttribute('property', 'og:title')
      document.head.appendChild(ogTitle)
    }
    ogTitle.setAttribute('content', title)

    const ogDescription = document.querySelector('meta[property="og:description"]') || document.createElement('meta')
    if (!document.querySelector('meta[property="og:description"]')) {
      ogDescription.setAttribute('property', 'og:description')
      document.head.appendChild(ogDescription)
    }
    ogDescription.setAttribute('content', description)

    const ogUrl = document.querySelector('meta[property="og:url"]') || document.createElement('meta')
    if (!document.querySelector('meta[property="og:url"]')) {
      ogUrl.setAttribute('property', 'og:url')
      document.head.appendChild(ogUrl)
    }
    if (shareUrl) {
      ogUrl.setAttribute('content', shareUrl)
    }

    const ogType = document.querySelector('meta[property="og:type"]') || document.createElement('meta')
    if (!document.querySelector('meta[property="og:type"]')) {
      ogType.setAttribute('property', 'og:type')
      document.head.appendChild(ogType)
    }
    ogType.setAttribute('content', 'website')

    // Open Graph Site Name
    const ogSiteName = document.querySelector('meta[property="og:site_name"]') || document.createElement('meta')
    if (!document.querySelector('meta[property="og:site_name"]')) {
      ogSiteName.setAttribute('property', 'og:site_name')
      document.head.appendChild(ogSiteName)
    }
    ogSiteName.setAttribute('content', 'TraderHub24')

    // Twitter Card tags
    const twitterCard = document.querySelector('meta[name="twitter:card"]') || document.createElement('meta')
    if (!document.querySelector('meta[name="twitter:card"]')) {
      twitterCard.setAttribute('name', 'twitter:card')
      document.head.appendChild(twitterCard)
    }
    twitterCard.setAttribute('content', 'summary_large_image')

    const twitterTitle = document.querySelector('meta[name="twitter:title"]') || document.createElement('meta')
    if (!document.querySelector('meta[name="twitter:title"]')) {
      twitterTitle.setAttribute('name', 'twitter:title')
      document.head.appendChild(twitterTitle)
    }
    twitterTitle.setAttribute('content', title)

    const twitterDescription = document.querySelector('meta[name="twitter:description"]') || document.createElement('meta')
    if (!document.querySelector('meta[name="twitter:description"]')) {
      twitterDescription.setAttribute('name', 'twitter:description')
      document.head.appendChild(twitterDescription)
    }
    twitterDescription.setAttribute('content', description)
  }, [bundle, shareUrl, showQuantity])

  const handleCopyLink = async () => {
    if (!shareUrl) return
    try {
      await navigator.clipboard.writeText(shareUrl)
      showSuccess('Link copied to clipboard')
    } catch {
      window.prompt('Copy this link:', shareUrl)
    }
  }

  const handleSubscribe = () => {
    // Send user to pricing + open signup flow
    navigate('/?signup=1#pricing')
  }

  return (
    <div className="min-h-screen bg-app-bg text-gray-900">
      <header className="w-full py-4 md:py-6 px-4 md:px-6 max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img
              src={logo}
              alt="TraderHub24"
              className="h-10 md:h-12 w-auto cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => navigate('/')}
            />
            <div>
              <p className="text-sm font-semibold text-gray-900">Public Trade</p>
              <p className="text-[10px] md:text-xs text-gray-500">{isUuid ? 'Shared via community' : 'Shared via link'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {shareUrl && (
              <button
                onClick={handleCopyLink}
                className="flex-1 sm:flex-none px-4 py-2 text-sm font-medium text-white bg-orange-500 border border-orange-500 rounded-lg hover:bg-orange-600 transition-colors text-center"
              >
                Copy link
              </button>
            )}
            <button
              onClick={() => navigate(-1)}
              className="flex-1 sm:flex-none px-4 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-center"
            >
              Back
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-6 pb-16">
        {loading ? (
          <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-6">
            <p className="text-gray-500 text-center">Loading trade...</p>
          </div>
        ) : error ? (
          <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-6">
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          </div>
        ) : bundle ? (
          <div className="space-y-6">
            {/* Publisher Profile Card */}
            {bundle.publisher && (
              <section className="bg-app-box border border-gray-200 rounded-2xl shadow-sm p-4 md:p-6">
                <div className="flex flex-col md:flex-row gap-4 md:gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-orange-500 flex items-center justify-center">
                      <span className="text-white text-2xl md:text-3xl font-bold">
                        {bundle.publisher.display_name
                          ? bundle.publisher.display_name.charAt(0).toUpperCase()
                          : '?'}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                      {bundle.publisher.display_name || 'Trader'}
                    </h2>
                    {bundle.publisher.biography && (
                      <p className="text-sm text-gray-700 mb-4 whitespace-pre-wrap">
                        {bundle.publisher.biography}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-3">
                      {bundle.publisher.x_profile && (
                        <a
                          href={
                            bundle.publisher.x_profile.startsWith('http')
                              ? bundle.publisher.x_profile
                              : `https://x.com/${bundle.publisher.x_profile.replace('@', '')}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm text-gray-700"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                          </svg>
                          <span>X</span>
                        </a>
                      )}
                      {bundle.publisher.instagram_profile && (
                        <a
                          href={
                            bundle.publisher.instagram_profile.startsWith('http')
                              ? bundle.publisher.instagram_profile
                              : `https://instagram.com/${bundle.publisher.instagram_profile.replace('@', '')}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm text-gray-700"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                          </svg>
                          <span>Instagram</span>
                        </a>
                      )}
                      {bundle.publisher.telegram_profile && (
                        <a
                          href={
                            bundle.publisher.telegram_profile.startsWith('http')
                              ? bundle.publisher.telegram_profile
                              : `https://t.me/${bundle.publisher.telegram_profile.replace('@', '')}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm text-gray-700"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                          </svg>
                          <span>Telegram</span>
                        </a>
                      )}
                      {bundle.publisher.youtube_profile && (
                        <a
                          href={
                            bundle.publisher.youtube_profile.startsWith('http')
                              ? bundle.publisher.youtube_profile
                              : `https://youtube.com/${bundle.publisher.youtube_profile}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm text-gray-700"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                          </svg>
                          <span>YouTube</span>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </section>
            )}

            <section className="bg-app-box border border-gray-200 rounded-2xl shadow-sm p-4 md:p-6">
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{bundle.trade.symbol}</h1>
                  <p className="text-sm text-gray-600 mt-1">
                    {bundle.trade.market} • {bundle.trade.trade_type.toUpperCase()} •{' '}
                    {bundle.trade.long_short.toUpperCase()}
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    Entry: {formatDateTime(bundle.trade.entry_date)}
                    {bundle.trade.exit_date ? ` • Exit: ${formatDateTime(bundle.trade.exit_date)}` : ''}
                  </p>
                </div>
                <div className="text-left md:text-right p-3 md:p-0 bg-gray-50 md:bg-transparent rounded-xl border border-gray-100 md:border-0">
                  <div className="text-xs text-gray-500">{showQuantity ? 'P&L' : 'P&L %'}</div>
                  {showQuantity && (
                    <div
                      className={`text-xl md:text-2xl font-bold ${
                        bundle.trade.pnl !== null && bundle.trade.pnl >= 0 ? 'text-green-700' : 'text-red-600'
                      }`}
                    >
                      {formatNumber(bundle.trade.pnl, bundle.trade.currency)}
                    </div>
                  )}
                  <div className="text-xs text-gray-500 mt-1">
                    P&L %: {bundle.trade.pnl_percentage !== null ? `${bundle.trade.pnl_percentage.toFixed(2)}%` : '-'}
                  </div>
                  <div className="text-[10px] text-red-600 mt-2 font-medium">
                    {t('publicTrade.notInvestmentAdvice')}
                  </div>
                </div>
              </div>

              {/* Current Market Data Section for Open Trades - Moved to top */}
              {!bundle.trade.exit_date && bundle.trade.current_price && (
                <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <h3 className="text-sm font-bold text-blue-800 mb-3">Current Market Data</h3>
                  <div className={`grid grid-cols-2 ${showQuantity ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-4`}>
                    <div>
                      <p className="text-xs text-gray-500">Current Price</p>
                      <p className="text-lg font-bold text-gray-900">
                        {formatNumber(bundle.trade.current_price, bundle.trade.currency)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Daily Change</p>
                      <p
                        className={`text-lg font-bold ${
                          bundle.trade.daily_change_percentage && bundle.trade.daily_change_percentage >= 0
                            ? 'text-green-600'
                            : 'text-red-600'
                        }`}
                      >
                        {bundle.trade.daily_change_percentage !== null && bundle.trade.daily_change_percentage !== undefined
                          ? `${bundle.trade.daily_change_percentage >= 0 ? '+' : ''}${bundle.trade.daily_change_percentage.toFixed(2).replace('.', ',')}%`
                          : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Unrealized P&L %</p>
                      <p
                        className={`text-lg font-bold ${
                          bundle.trade.unrealized_pnl_percentage && bundle.trade.unrealized_pnl_percentage >= 0
                            ? 'text-green-600'
                            : 'text-red-600'
                        }`}
                      >
                        {bundle.trade.unrealized_pnl_percentage !== null && bundle.trade.unrealized_pnl_percentage !== undefined
                          ? `${bundle.trade.unrealized_pnl_percentage >= 0 ? '+' : ''}${bundle.trade.unrealized_pnl_percentage.toFixed(2).replace('.', ',')}%`
                          : '-'}
                      </p>
                    </div>
                    {showQuantity && (
                      <div>
                        <p className="text-xs text-gray-500">Unrealized P&L</p>
                        <p
                          className={`text-lg font-bold ${
                            bundle.trade.unrealized_pnl && bundle.trade.unrealized_pnl >= 0
                              ? 'text-green-600'
                              : 'text-red-600'
                          }`}
                        >
                          {formatNumber(bundle.trade.unrealized_pnl, bundle.trade.currency)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Trade Summary Section for Closed Trades */}
              {bundle.trade.exit_date && (
                <div className={`mt-6 ${
                  bundle.trade.pnl !== null && bundle.trade.pnl >= 0 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-red-50 border-red-200'
                } border rounded-xl p-4`}>
                  <h3 className={`text-sm font-bold mb-3 ${
                    bundle.trade.pnl !== null && bundle.trade.pnl >= 0 ? 'text-green-800' : 'text-red-800'
                  }`}>Trade Summary</h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">Entry</p>
                      <p className="text-lg font-bold text-gray-900">
                        {formatNumber(bundle.trade.entry_price, bundle.trade.currency)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Average Cost</p>
                      <p className="text-lg font-bold text-blue-700">
                        {formatNumber(averageCost, bundle.trade.currency)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Exit</p>
                      <p className="text-lg font-bold text-gray-900">
                        {formatNumber(bundle.trade.exit_price, bundle.trade.currency)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Realized RR</p>
                      <p
                        className={`text-lg font-bold ${
                          realizedRR === null ? 'text-gray-500' : isRealizedProfit ? 'text-green-700' : 'text-red-600'
                        }`}
                      >
                        {realizedRR !== null ? `${realizedRR >= 0 ? '+' : ''}${realizedRR.toFixed(2)}R` : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">P&L %</p>
                      <p
                        className={`text-lg font-bold ${
                          bundle.trade.pnl_percentage !== null && bundle.trade.pnl_percentage >= 0 ? 'text-green-700' : 'text-red-600'
                        }`}
                      >
                        {bundle.trade.pnl_percentage !== null 
                          ? `${bundle.trade.pnl_percentage >= 0 ? '+' : ''}${bundle.trade.pnl_percentage.toFixed(2)}%` 
                          : '-'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Trade Details Grid - Colorful boxes like TradeDetail */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mt-6">
                <div className="bg-orange-50 rounded-xl p-3 md:p-4 border border-orange-100">
                  <p className="text-[10px] md:text-xs text-gray-500 uppercase tracking-wider">Entry Price</p>
                  <p className="text-lg md:text-xl font-bold text-orange-700">
                    {formatNumber(bundle.trade.entry_price, bundle.trade.currency)}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">{formatDateTime(bundle.trade.entry_date)}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 md:p-4 border border-gray-200">
                  <p className="text-[10px] md:text-xs text-gray-500 uppercase tracking-wider">Stop Loss</p>
                  <p className="text-lg md:text-xl font-semibold text-gray-900">
                    {formatNumber(bundle.trade.stop_loss, bundle.trade.currency)}
                  </p>
                  <p className="text-xs text-red-500 mt-1">R/R: {bundle.trade.rr ?? 'N/A'}</p>
                </div>
                <div className="bg-green-50 rounded-xl p-3 md:p-4 border border-green-100">
                  <p className="text-[10px] md:text-xs text-gray-500 uppercase tracking-wider">Take Profit</p>
                  <p className="text-lg md:text-xl font-semibold text-green-700">
                    {formatNumber(bundle.trade.take_profit, bundle.trade.currency)}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">Target: {bundle.trade.rr ? `${bundle.trade.rr}R` : 'N/A'}</p>
                </div>
              </div>

              {/* Second row - Average Cost, Position Size, Realized RR */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mt-4">
                <div className="bg-blue-50 rounded-xl p-3 md:p-4 border border-blue-100">
                  <p className="text-[10px] md:text-xs text-gray-500 uppercase tracking-wider">Average Cost</p>
                  <p className="text-lg md:text-xl font-semibold text-blue-700">
                    {formatNumber(averageCost, bundle.trade.currency)}
                  </p>
                </div>
                <div className="bg-purple-50 rounded-xl p-3 md:p-4 border border-purple-100">
                  <p className="text-[10px] md:text-xs text-gray-500 uppercase tracking-wider">Position Size</p>
                  {showQuantity ? (
                    <p className="text-lg md:text-xl font-semibold text-purple-700">
                      {formatNumber(bundle.trade.position_size, bundle.trade.currency)}
                    </p>
                  ) : (
                    <p className="text-xs md:text-sm text-gray-500 mt-1">{t('publicTrade.dataNotShared')}</p>
                  )}
                </div>
                <div className={`rounded-xl p-3 md:p-4 border ${
                  bundle.trade.exit_date 
                    ? (isRealizedProfit ? 'bg-green-50 border-green-100' : 'bg-red-50 border-red-100')
                    : 'bg-gray-50 border-gray-200'
                }`}>
                  <p className="text-[10px] md:text-xs text-gray-500 uppercase tracking-wider">Realized RR</p>
                  <p
                    className={`text-lg md:text-xl font-semibold ${
                      realizedRR === null ? 'text-gray-500' : isRealizedProfit ? 'text-green-700' : 'text-red-600'
                    }`}
                  >
                    {realizedRR !== null ? `${realizedRR >= 0 ? '+' : ''}${realizedRR.toFixed(2)}R` : '-'}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    {bundle.trade.exit_date ? 'Trade closed' : 'Open trade'}
                  </p>
                </div>
              </div>

              {/* Quantity row - only if showQuantity */}
              {showQuantity && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mt-4">
                  <div className="bg-gray-50 rounded-xl p-3 md:p-4 border border-gray-200">
                    <p className="text-[10px] md:text-xs text-gray-500 uppercase tracking-wider">Quantity</p>
                    <p className="text-lg md:text-xl font-semibold text-gray-900">
                      {formatNumber(bundle.trade.quantity, null, 4)}
                    </p>
                  </div>
                </div>
              )}

              {(bundle.trade.trade_setup || bundle.trade.notes) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  {bundle.trade.trade_setup && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Setup</p>
                      <p className="text-base text-gray-900 mt-1">{bundle.trade.trade_setup}</p>
                    </div>
                  )}
                  {bundle.trade.notes && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Notes</p>
                      <p className="text-base text-gray-900 mt-1 whitespace-pre-wrap">{bundle.trade.notes}</p>
                    </div>
                  )}
                </div>
              )}
            </section>

            <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-app-box border border-gray-200 rounded-2xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">{t('publicTrade.tradeHistory')}</h2>
                  <span className="text-xs text-gray-500">{bundle.history.length} events</span>
                </div>

                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-red-900 mb-2">{t('publicTrade.riskWarning')}</h3>
                      <p className="text-sm text-red-800 mb-2 font-medium">{t('publicTrade.notInvestmentAdvice')}</p>
                      <p className="text-xs text-red-700">{t('publicTrade.pnlDisclaimer')}</p>
                    </div>
                  </div>
                </div>

                {bundle.history.length === 0 ? (
                  <p className="text-sm text-gray-500">No events shared for this trade.</p>
                ) : (
                  <div className="space-y-4">
                    {bundle.history.map((item, idx) => (
                      <div key={`${item.created_at}-${idx}`} className="flex gap-3">
                        <div className="flex flex-col items-center">
                          <div className="w-3 h-3 rounded-full bg-orange-500 mt-1" />
                          {idx !== bundle.history.length - 1 && <div className="flex-1 w-px bg-gray-200" />}
                        </div>
                        <div className="flex-1 pb-4 border-b border-gray-100 last:border-b-0 last:pb-0">
                          <p className="text-xs text-gray-500">{formatDateTime(item.created_at)}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <p className="text-base font-semibold text-gray-900">{titleizeEventType(item.event_type)}</p>
                            <span className="px-2 py-0.5 rounded-full text-[11px] bg-gray-100 text-gray-700">
                              {item.event_type}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{item.description}</p>
                          {item.price !== null && (
                            <p className="text-sm font-medium text-orange-600 mt-1">
                              Price: {formatNumber(item.price, bundle.trade.currency)}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-app-box border border-gray-200 rounded-2xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-semibold text-gray-900">Comments</h2>
                  <span className="text-xs text-gray-500">{bundle.comments.length} entries</span>
                </div>

                {bundle.comments.length === 0 ? (
                  <p className="text-sm text-gray-500">No comments shared for this trade.</p>
                ) : (
                  <div className="space-y-3">
                    {bundle.comments.map((c, idx) => (
                      <div key={`${c.created_at}-${idx}`} className="p-3 rounded-xl bg-gray-50 border border-gray-100">
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-gray-500">{formatDateTime(c.created_at)}</p>
                          <span className="text-xs text-orange-600">{c.mood ?? 'Neutral'}</span>
                        </div>
                        <p className="text-sm text-gray-800 mt-2 whitespace-pre-wrap">{c.text}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>

            <section className="bg-orange-50 border border-orange-200 rounded-2xl p-4 md:p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-center md:text-left">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Want to see all details of this trade?</h3>
                  <p className="text-sm text-gray-700 mt-1">
                    Subscribe to TraderHub24 for free and get live notifications on your account
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleSubscribe}
                  className="w-full md:w-auto px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition-colors shadow-sm text-center"
                >
                  Subscribe Free
                </button>
              </div>
            </section>
          </div>
        ) : null}
      </main>
    </div>
  )
}
